/**
 * useGulirAnchor — perbaikan gulir ke anchor sehalaman (#alat, #harga,
 * #bantuan, dst) untuk halaman ber-`content-visibility:auto`.
 *
 * Dipanggil SEKALI per layout (default.vue DAN ringan.vue — keduanya
 * memakai section `.ca-tunda` yang sama, jadi keduanya kena bug yang sama).
 * JANGAN salin-tempel isinya ke layout baru; panggil composable ini saja.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * BUG YANG DIPERBAIKI (terbukti reproduksi live 2026-08-06, klien maupun
 * reload penuh ke URL ber-hash)
 * ────────────────────────────────────────────────────────────────────────────
 * Section panjang di halaman ini memakai `content-visibility:auto` (.ca-tunda,
 * lihat main-token.css) supaya isi di luar layar tidak dirender — bagus untuk
 * cat pertama, tapi merusak lompatan LANGSUNG ke anchor jauh (mis. #harga,
 * #bantuan) lewat DUA bug sekaligus:
 *
 *  1. SALAH POSISI — tinggi section yang belum dirender dipatok TAKSIRAN
 *     `contain-intrinsic-size: 640px`, padahal tinggi asli 580–840px
 *     tergantung isi. Posisi gulir yang dihitung dari taksiran itu meleset;
 *     begitu section di atas tujuan benar-benar dirender, seluruh isi di
 *     bawahnya bergeser dan viewport tertinggal di CELAH KOSONG.
 *  2. LAYAR BEKU — browser "mengejar" merender SEMUA section yang terlewati
 *     sekaligus tepat saat melompat, satu operasi layout raksasa yang
 *     sinkron. Terbukti live: tab sampai tidak merespons beberapa detik
 *     (bahkan eksekusi JS ikut tersendat) persis setelah lompat.
 *
 * Klik terasa "berkedip lalu tidak terjadi apa-apa" — gabungan keduanya.
 *
 * Perbaikannya BUKAN menghitung ulang berkali-kali (percobaan pertama —
 * memperbaiki #1 tapi #2 tetap terjadi), tapi menghilangkan akar masalahnya:
 * lepas `content-visibility` SEBELUM menghitung apa pun, supaya kerja
 * rendernya kelar sebelum gulir dimulai, bukan mendadak di tengah lompatan.
 * Sekali dibuka TIDAK ditutup lagi — pengguna yang sudah memakai navigasi
 * anchor sudah menunjukkan niat menjelajah; kehilangan optimasi
 * cat-di-luar-layar untuk sisa sesi ini adalah trade-off wajar dibanding
 * tombol yang macet.
 */

const OFFSET_HEADER = 80

/**
 * `selector` berasal dari `route.hash` — bagian URL yang APA SAJA bisa
 * ditaruh pengunjung di sana (tautan salah ketik, fragment pelacakan dari
 * kampanye iklan, tautan yang disalin-tempel manual). `document.querySelector`
 * memperlakukannya sebagai selektor CSS mentah; hash yang bukan CSS
 * ident-token yang sah (mis. diawali angka: "#3nomor") membuatnya MELEMPAR
 * `DOMException`, bukan sekadar mengembalikan null. Try/catch di sini —
 * bukan validasi format di awal — supaya SEMUA bentuk hash aneh aman,
 * bukan cuma yang kepikiran sekarang.
 */
function cariAman(selector: string): Element | null {
  try {
    return document.querySelector(selector)
  } catch {
    return null
  }
}

function bukaTundaLaluGulir(selector: string, perilaku: () => ScrollBehavior) {
  document.querySelectorAll<HTMLElement>('.ca-tunda').forEach((el) => {
    el.style.contentVisibility = 'visible'
  })

  const gulir = () => {
    const target = cariAman(selector)
    if (!target) return
    const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - OFFSET_HEADER)
    window.scrollTo({ top, behavior: perilaku() })
  }

  // `requestAnimationFrame` susulan HANYA jaring pengaman untuk pergeseran
  // kecil di luar content-visibility (mis. gambar tanpa dimensi eksplisit) —
  // bacaan pertama dari `getBoundingClientRect()` sudah akurat karena
  // memaksa reflow sinkron dengan style yang BARU saja dilepas.
  gulir()
  requestAnimationFrame(gulir)
}

/**
 * Klik tautan ke hash yang SAMA dengan `location.hash` saat ini adalah
 * NO-OP bagi vue-router — rute (path+hash) dianggap tidak berubah, jadi
 * `router.options.scrollBehavior` di atas TIDAK PERNAH terpanggil.
 *
 * Skenario nyata (terbukti reproduksi live 2026-08-06): pengunjung klik CTA
 * ke #harga (mendarat benar), lalu GULIR MANUAL kembali ke atas untuk
 * membandingkan sesuatu (hash TETAP #harga — gulir manual tidak mengubah
 * URL), lalu klik tombol yang sama lagi mengharap dibawa balik ke bagian
 * harga. Karena hash tidak berubah, vue-router diam — klik terasa
 * "berkedip lalu tidak terjadi apa-apa" TANPA sempat menyentuh
 * scrollBehavior sama sekali, persis gejala yang sudah "diperbaiki"
 * sebelumnya tapi jalur ini belum tertutup.
 *
 * Listener fase-CAPTURE di level dokumen, independen dari navigasi
 * vue-router: baca hash tujuan dari `href` yang sudah di-resolve
 * (menangani baik `to="#harga"` maupun `to="/#harga"`), dan kalau sama
 * dengan hash saat ini DI HALAMAN YANG SAMA, jalankan koreksinya sendiri.
 * Untuk hash yang benar-benar BERUBA (kasus umum), biarkan
 * scrollBehavior yang menangani — di sini tidak melakukan apa-apa supaya
 * tidak dobel-gulir.
 */
function pasangKoreksiKlikSamaHash(perilaku: () => ScrollBehavior) {
  const tangani = (e: MouseEvent) => {
    const a = (e.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement | null
    if (!a) return
    let tujuan: URL
    try {
      tujuan = new URL(a.href, location.href)
    } catch {
      return
    }
    if (!tujuan.hash || tujuan.pathname !== location.pathname) return
    if (tujuan.hash === location.hash) {
      bukaTundaLaluGulir(tujuan.hash, perilaku)
    }
  }
  document.addEventListener('click', tangani, true)
  onBeforeUnmount(() => document.removeEventListener('click', tangani, true))
}

export function useGulirAnchor() {
  onMounted(() => {
    const router = useRouter()
    const nuxtApp = useNuxtApp()
    const route = useRoute()
    const perilaku = (): ScrollBehavior =>
      (window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth') as ScrollBehavior

    pasangKoreksiKlikSamaHash(perilaku)

    router.options.scrollBehavior = (to, from, saved) => {
      if (saved) return saved
      if (to.hash) {
        // Kita urus gulirnya sendiri (return false) — mekanisme
        // posisi-sekali-hitung bawaan router tidak cukup untuk section
        // ber-content-visibility (lihat komentar bukaTundaLaluGulir di atas).
        if (to.path !== from.path) {
          // Lintas-halaman: elemen tujuan baru ada setelah halaman baru dirender.
          nuxtApp.hooks.hookOnce('page:finish', () =>
            setTimeout(() => bukaTundaLaluGulir(to.hash, perilaku), 0)
          )
        } else {
          bukaTundaLaluGulir(to.hash, perilaku)
        }
        return false
      }
      if (to.path !== from.path) return { top: 0, left: 0 }
      return false
    }

    // MUAT PERTAMA dengan hash sudah di URL (mis. buka/refresh langsung ke
    // /#harga, atau berpindah dari layout LAIN yang belum memanggil composable
    // ini — lihat ringan.vue) TIDAK lewat scrollBehavior di atas sama sekali —
    // override itu baru terpasang saat onMounted ini jalan, setelah navigasi
    // pertama sudah kelar. Fragment-scroll BAWAAN browser sudah lebih dulu
    // menembak posisi dari layout .ca-tunda yang masih taksiran (bug yang sama
    // seperti di bukaTundaLaluGulir), dan karena hash-nya SUDAH cocok, klik
    // ulang tautan yang sama nanti jadi no-op bagi vue-router (tidak dianggap
    // navigasi baru) — pengguna bisa mendarat salah tempat dan terjebak di
    // situ. Koreksi begitu layout ini mount, kalau memang ada hash yang harus
    // dituju.
    if (route.hash) {
      bukaTundaLaluGulir(route.hash, perilaku)
    }
  })
}
